import { useUser } from '@application/authentication/use_user';
import { withTranslation } from '@application/i18n/with_translation';
import { UnitService } from '@application/unit.service';
import { UniversalConfig } from '@application/universal_config.service';
import { BadgeNumber, Icon, IconDictionary, Tooltip } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { useCountedIssues } from '@feature/issues/use_counted_issues';
import { useResolve } from '@visiba/instantiation';
import { useWindowSize } from '@visiba/react-std';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const StyledNavRoot = styled.nav`
	width: 100%;
	display: flex;
	gap: ${tokens.spacing.x1};
	overflow: hidden;

	${tokens.breakpointQuery.medium} {
		flex-direction: row;
		gap: ${tokens.spacing.x2};
	}
`;

const StyledButton = styled(Link)`
	flex-direction: column;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 50px;
	padding: ${tokens.spacing.x0_5} ${tokens.spacing.x1_5};
	position: relative;
	color: ${tokens.color.content.default};
	border-radius: ${tokens.borderRadius.xSmall};
	transition: background 150ms ease-in;
	overflow: hidden;

	@media (hover: hover) {
		&:hover {
			background: ${tokens.color.bg.interactiveMutedHover};
		}
	}

	&:active {
		background: ${tokens.color.bg.interactiveMutedActive};
	}

	span {
		font: ${tokens.text.label.xSmallRegular};
		display: block;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		text-align: center;
		width: 100%;
		height: 24px;
		margin-top: 2px;
	}

	& .badge {
		position: absolute;
		top: -6px;
		right: -16px;
	}

	& .iconArea {
		flex-shrink: 0;
		position: relative;
	}

	&:focus-visible {
		outline: 2px solid ${tokens.color.focus};
		outline-offset: -2px;
		border-radius: ${tokens.borderRadius.small};
	}

	&[aria-current='page'] {
		color: ${tokens.color.content.interactive};

		${tokens.breakpointQuery.medium} {
			color: ${tokens.color.content.brand};
		}
	}

	${tokens.breakpointQuery.medium} {
		height: unset;
		width: unset;
		flex-direction: row;
		justify-content: start;
		gap: ${tokens.size.x1_5};
		min-height: ${tokens.size.x6};
		border-radius: ${tokens.borderRadius.medium};
		transition: background 150ms ease-in;
		flex: 1 1 auto;
		min-width: 120px;

		span {
			font: ${tokens.text.label.regular};
		}

		&:hover {
			background: ${tokens.color.bg.interactiveMutedHover};
		}

		&[aria-current='page'] {
			span {
				font: ${tokens.text.label.semiBold};
			}
		}

		& .badge {
			top: -12px;
			right: -12px;
		}
	}
`;

function NavItemVisits() {
	const countedIssues = useCountedIssues(['ongoing', 'completed']);

	return <NavItem to='/visits' icon='archive' label={withTranslation('Component.Nav.PatientCases')} countedIssues={countedIssues} />;
}

function NavItemSearchReception() {
	const unitService = useResolve(UnitService);

	if (unitService.singleEntrance) return null;

	return <NavItem to='/' icon='magnifyingGlass' label={withTranslation('Component.Nav.SearchReception')} />;
}

function NavItemReception() {
	const universalConfig = useResolve(UniversalConfig);
	const unitService = useResolve(UnitService);

	let receptionId: number | null = null;

	if (universalConfig.recentlyVisitedReceptionId.value != null) {
		receptionId = universalConfig.recentlyVisitedReceptionId.value;
	} else {
		const unit = unitService.getUnitNodeInTree(universalConfig.selectedUnitId);

		receptionId = unit.isEntrance ? unit.receptionId : null;
	}

	if (!receptionId) {
		return;
	}

	const unit = unitService.getUnitNodeInTreeByReceptionId(receptionId);

	return <NavItem to={'/' + unit.routeWithoutLicenceholder} icon='house' label={unit.name} />;
}

function NavItemUser() {
	const user = useUser();
	if (user != null) return <NavItem to='/settings/my-account' icon='user' label={user.commonName} />;

	return <NavItem to='/sign-in' icon='user' label={withTranslation('Common.Button.SignIn')} />;
}

interface NavItemProps {
	to: string;
	icon?: keyof IconDictionary;
	label: string;
	countedIssues?: number;
}

function NavItem({ countedIssues = 0, to, label, icon }: NavItemProps) {
	const [isTruncated, setIsTruncated] = useState(false);
	const location = useLocation();
	const textElementRef = useRef<HTMLSpanElement>(null);

	const size = useWindowSize(250);
	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	useEffect(
		function checkIfTextIsTruncated() {
			if (textElementRef.current == null) return;

			setIsTruncated(textElementRef.current.clientWidth < textElementRef.current.scrollWidth);
		},
		[size, label],
	);

	const link = (
		<StyledButton aria-current={location.pathname === to ? 'page' : undefined} to={to}>
			<div className='iconArea'>
				{icon ? <Icon type={icon} /> : null}
				{countedIssues > 0 ? (
					<div className='badge'>
						<BadgeNumber variant='interactive' label='Notiser' value={countedIssues} size='medium' />
					</div>
				) : null}
			</div>
			<span data-text={label} ref={textElementRef}>
				{label}
			</span>
		</StyledButton>
	);

	if (isTruncated) {
		return <Tooltip content={label}>{link}</Tooltip>;
	}
	return <>{link}</>;
}

export const Nav = {
	Root: (p: React.PropsWithChildren) => <>{p.children}</>,
	Content: StyledNavRoot,
	ItemSearchReception: NavItemSearchReception,
	ItemReception: NavItemReception,
	ItemVisits: NavItemVisits,
	ItemUser: NavItemUser,
	Item: NavItem,
} as const;
